"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";
import { ChatOtpCard } from "@/components/quick-flow/ergaenzen/ChatOtpCard";
import type { ChatFlowProgress } from "@/lib/chat/flow-progress";
interface ChatFlowFooterProps {
  progress: ChatFlowProgress;
  /** Called with the entity id once the user completes the confirmation. */
  onVerified: (entityId: string) => void;
}

/**
 * The chat's pinned flow footer — where the user is, and what is left to do.
 *
 * WHY IT IS PINNED. Klara creates the row as soon as she knows role + location,
 * long before the dialogue ends. Until now the email confirmation that follows
 * from that was rendered INSIDE the message bubble that produced it, so every
 * later turn pushed it further up a list that auto-scrolls to the bottom. In
 * prod conversation c6efd18e (2026-08-24) it went out of view under eight
 * turns; the seller wrote "Ich finde keine bestätigungskasten", was given
 * directions Klara could not actually verify, and took a callback instead. His
 * offering still sits at `pending_email_verification` with no email on it at
 * all — which also means the hourly UnverifiedEntityService could never reach
 * him, because its reminder resolves `users.email`.
 *
 * So this lives OUTSIDE the scroll container (see ChatPanel), between the
 * transcript and the input. It cannot scroll away, which is what makes it safe
 * for nobody — Klara included — to ever describe where it is.
 *
 * WHY IT SHOWS PROGRESS. Early capture used to leak into the conversation as a
 * state the user had to reason about: something exists, it is not active, there
 * is a box to confirm somewhere. None of that is theirs to know. The same early
 * row instead drives a visible path in the funnel's own vocabulary — Standort →
 * Angebot/Menge → Kontakt (see lib/chat/flow-progress.ts) — so a user who
 * abandoned /schnellangebot and asked Klara instead recognises where they are.
 *
 * WHY THE CONFIRMATION SHOWS AT ONCE. It used to be withheld until nothing else
 * was outstanding, one ask at a time. But a draft does not always complete: a
 * seller with unassessed land cannot name Menge or Preis, and a buyer may not
 * know the Budget. On 2026-09-25 that cost two leads within 80 minutes
 * (offering 60827505, request cd672a9f). The card never appeared, the draft
 * tools had Klara say the input was ready, and each user typed their address
 * into the chat, where it reached no field. So the card shows as soon as the
 * draft needs a confirmation; the strip still names the step Klara is on.
 */
export function ChatFlowFooter(t0) {
  const $ = _c(37);
  const {
    progress,
    onVerified
  } = t0;
  const {
    steps,
    stepIndex,
    entityId,
    entityType,
    hasVerificationStep
  } = progress;
  const impressionSentRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== entityId || $[1] !== entityType || $[2] !== hasVerificationStep) {
    t1 = () => {
      if (!hasVerificationStep || impressionSentRef.current) {
        return;
      }
      impressionSentRef.current = true;
      trackEvent("guest_otp_card_shown", {
        entity_type: entityType,
        entity_id: entityId
      });
    };
    t2 = [hasVerificationStep, entityType, entityId];
    $[0] = entityId;
    $[1] = entityType;
    $[2] = hasVerificationStep;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (progress.isComplete && !hasVerificationStep) {
    return null;
  }
  const currentLabel = progress.isComplete ? "Fertig" : steps[stepIndex];
  const currentNumber = Math.min(stepIndex + 1, steps.length);
  const t3 = steps.length;
  const t4 = `Schritt ${currentNumber} von ${steps.length}: ${currentLabel}`;
  let t5;
  if ($[5] !== currentNumber || $[6] !== steps.length) {
    t5 = <span className="text-[10px] font-medium tracking-widest uppercase text-on-surface-variant">Schritt {currentNumber} von {steps.length}</span>;
    $[5] = currentNumber;
    $[6] = steps.length;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== currentLabel) {
    t6 = <span className="text-xs font-bold text-primary truncate">{currentLabel}</span>;
    $[8] = currentLabel;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="flex items-baseline justify-between gap-2">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== currentNumber || $[14] !== progress.isComplete || $[15] !== steps) {
    let t9;
    if ($[17] !== currentNumber || $[18] !== progress.isComplete) {
      t9 = (step, i) => <div key={step} className={`flex-1 rounded-full transition-colors ${i < currentNumber || progress.isComplete ? "bg-secondary" : "bg-outline-variant"}`} />;
      $[17] = currentNumber;
      $[18] = progress.isComplete;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = steps.map(t9);
    $[13] = currentNumber;
    $[14] = progress.isComplete;
    $[15] = steps;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="mt-1.5 flex gap-1 h-1" aria-hidden={true}>{t8}</div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== currentNumber || $[23] !== steps.length || $[24] !== t4 || $[25] !== t7 || $[26] !== t9) {
    t10 = <div role="progressbar" aria-valuenow={currentNumber} aria-valuemin={1} aria-valuemax={t3} aria-label={t4}>{t7}{t9}</div>;
    $[22] = currentNumber;
    $[23] = steps.length;
    $[24] = t4;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== entityId || $[29] !== entityType || $[30] !== hasVerificationStep || $[31] !== onVerified || $[32] !== progress.needsVerification) {
    t11 = hasVerificationStep && <div className="mt-2.5"><ChatOtpCard entityId={entityId} entityType={entityType} initialVerified={!progress.needsVerification} onVerified={() => onVerified(entityId)} /></div>;
    $[28] = entityId;
    $[29] = entityType;
    $[30] = hasVerificationStep;
    $[31] = onVerified;
    $[32] = progress.needsVerification;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <div className="shrink-0 max-h-[55%] overflow-y-auto border-t border-surface-container bg-surface-container-low px-4 py-2.5">{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}